import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import type { GalleryPhoto, Photo } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Filesystem } from '@capacitor/filesystem';
import { Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { MAX_FILE_SIZE_BYTES } from 'common/constans/common.constants';
import { getBase64 } from 'common/helpers/getBase64.helper';
import { notification } from 'common/utils/notification';

const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png']);
const ACCEPT_ATTRIBUTE = '.jpg,.jpeg,.png,image/jpeg,image/png';

type NativePhoto = Pick<Photo | GalleryPhoto, 'format' | 'path' | 'webPath'>;

interface IUploadPhoto {
  currentUrl?: string | null;
}

const isAcceptedImage = (file: File) => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  return ACCEPTED_IMAGE_TYPES.has(file.type.toLowerCase()) || extension === 'jpg' || extension === 'jpeg' || extension === 'png';
};

const isCameraCancellation = (error: unknown) => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error && 'message' in error
      ? String(error.message)
      : String(error);

  return /cancelled|canceled|user cancel/i.test(message);
};

const base64ToBlob = (value: string, mimeType: string) => {
  const base64 = value.includes(',') ? value.slice(value.indexOf(',') + 1) : value;
  const binary = atob(base64.replace(/\s/g, ''));
  const chunkSize = 1024;
  const chunks: ArrayBuffer[] = [];

  for (let offset = 0; offset < binary.length; offset += chunkSize) {
    const chunk = binary.slice(offset, offset + chunkSize);
    const buffer = new ArrayBuffer(chunk.length);
    const bytes = new Uint8Array(buffer);

    for (let index = 0; index < chunk.length; index += 1) {
      bytes[index] = chunk.charCodeAt(index);
    }

    chunks.push(buffer);
  }

  return new Blob(chunks, { type: mimeType });
};

const getPhotoFormat = (photo: NativePhoto) => {
  const format = photo.format.toLowerCase();

  if (format === 'jpg' || format === 'jpeg') {
    return {
      extension: 'jpg',
      mimeType: 'image/jpeg',
    };
  }

  return {
    extension: format,
    mimeType: `image/${format}`,
  };
};

const getFileName = (extension: string) => {
  const date = new Date();
  const datePart = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
  const timePart = [
    String(date.getHours()).padStart(2, '0'),
    String(date.getMinutes()).padStart(2, '0'),
    String(date.getSeconds()).padStart(2, '0'),
  ].join('-');

  return `${datePart}_${timePart}.${extension}`;
};

const readNativePhoto = async (photo: NativePhoto): Promise<RcFile> => {
  if (!photo.path) {
    throw new Error('Camera did not return a native file path');
  }

  const { extension, mimeType } = getPhotoFormat(photo);
  const result = await Filesystem.readFile({ path: photo.path });

  if (typeof result.data !== 'string') {
    throw new Error('Camera returned an unsupported file payload');
  }

  const blob = base64ToBlob(result.data, mimeType);
  const uid = `native-${Date.now()}`;
  const file = new File([blob], getFileName(extension), {
    type: mimeType,
    lastModified: Date.now(),
  }) as RcFile;

  file.uid = uid;

  return file;
};

const getOriginalFile = (file: UploadFile): File | null => {
  if (file.originFileObj) {
    return file.originFileObj;
  }

  // Ant Design 5 can return the RcFile itself when beforeUpload returns false.
  return file instanceof File ? file : null;
};

export const useUploadPhoto = ({ currentUrl }: IUploadPhoto) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  useEffect(() => {
    if (!currentUrl) {
      setFileList([]);
      return;
    }

    const path = currentUrl.split('/');
    const encodedName = path[path.length - 1].split('?')[0];
    const photoName = decodeURIComponent(encodedName) || 'photo';

    setFileList([
      {
        uid: `remote-${currentUrl}`,
        name: photoName,
        status: 'done',
        url: currentUrl,
        thumbUrl: currentUrl,
      },
    ]);
  }, [currentUrl]);

  const validateFile = useCallback((file: File) => {
    if (!isAcceptedImage(file)) {
      notification.error({
        message: 'Only JPG, JPEG and PNG images are allowed!',
      });
      return false;
    }

    if (file.size >= MAX_FILE_SIZE_BYTES) {
      notification.error({
        message: 'Image must be less than 5 MB!',
      });
      return false;
    }

    return true;
  }, []);

  const saveNativePhoto = useCallback(
    async (source: CameraSource) => {
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      try {
        const photo = await Camera.getPhoto({
          resultType: CameraResultType.Uri,
          source,
          quality: 100,
          allowEditing: false,
          correctOrientation: true,
          saveToGallery: false,
        });

        const file = await readNativePhoto(photo);

        if (!validateFile(file)) {
          return;
        }

        setFileList([
          {
            uid: file.uid,
            name: file.name,
            status: 'done',
            type: file.type,
            size: file.size,
            url: photo.webPath,
            thumbUrl: photo.webPath,
            originFileObj: file,
          },
        ]);
      } catch (error) {
        if (isCameraCancellation(error)) {
          return;
        }

        console.error('Camera error:', error);
        notification.error({
          message: source === CameraSource.Camera ? 'Could not take a photo.' : 'Could not select a photo.',
        });
      }
    },
    [validateFile],
  );

  const takePicture = useCallback(() => saveNativePhoto(CameraSource.Camera), [saveNativePhoto]);

  const selectFromGallery = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (!Capacitor.isPluginAvailable('Camera')) {
      notification.error({
        message: 'Camera plugin is not available.',
      });
      return;
    }

    try {
      const { photos } = await Camera.pickImages({
        quality: 100,
        limit: 1,
        correctOrientation: true,
      });

      const photo = photos[0];

      if (!photo) {
        return;
      }

      const file = await readNativePhoto(photo);

      if (!validateFile(file)) {
        return;
      }

      setFileList([
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          type: file.type,
          size: file.size,
          url: photo.webPath,
          thumbUrl: photo.webPath,
          originFileObj: file,
        },
      ]);
    } catch (error) {
      if (isCameraCancellation(error)) {
        return;
      }

      console.error('Photo Library error:', error);

      notification.error({
        message: error instanceof Error ? error.message : 'Could not select a photo.',
      });
    }
  }, [validateFile]);

  const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
    (file) => {
      if (!validateFile(file)) {
        return Upload.LIST_IGNORE;
      }

      return false;
    },
    [validateFile],
  );

  const onChange = useCallback<NonNullable<UploadProps['onChange']>>(
    ({ fileList: newFileList }) => {
      const lastFile = newFileList.at(-1);

      if (!lastFile) {
        setFileList([]);
        return;
      }

      const originalFile = getOriginalFile(lastFile);

      if (originalFile && !validateFile(originalFile)) {
        return;
      }

      setFileList([
        {
          ...lastFile,
          status: 'done',
        },
      ]);
    },
    [validateFile],
  );

  const handlePreview = useCallback(async (file: UploadFile) => {
    let url = file.url || file.thumbUrl;

    if (!url) {
      const originalFile = getOriginalFile(file);

      if (!originalFile) {
        return;
      }

      url = await getBase64(originalFile as RcFile);
    }

    setPreviewImage(url);
    setPreviewOpen(true);
  }, []);

  const getFile = useCallback(async (): Promise<File | null> => {
    const file = fileList[0];

    return file ? getOriginalFile(file) : null;
  }, [fileList]);

  const uploadProps = useMemo<UploadProps>(
    () => ({
      fileList,
      onChange,
      onPreview: handlePreview,
      beforeUpload,
      maxCount: 1,
      multiple: false,
      listType: 'picture',
      accept: ACCEPT_ATTRIBUTE,
    }),
    [beforeUpload, fileList, handlePreview, onChange],
  );

  return {
    fileList,
    setFileList,
    previewOpen,
    setPreviewOpen,
    previewImage,
    uploadProps,
    takePicture,
    selectFromGallery,
    getFile,
  };
};
